import { VStack, Center, Flex, Heading, Input, Button, useToast } from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import InputField from '../../components/InputField'
import Page from '../../components/Page'
import { Logo } from '../../components/shared-components/Logo'
import { trimValue } from '../../utils/text-formatter'
import styled from '@emotion/styled'
import {
  setClinicId,
  setOrganizationId,
} from '../../features/onboardingContext/OnboardingContextSlice'
import { createOrganizationByHPIO } from '../OrganizationProfilePage/OrganizationSlice'
import useApi from '../../common/utils/api/useApi'
import { routes } from '../routes'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import { useEffect } from 'react'
import { appDidFinishLaunching } from '../../app/appSlice'
import { useAppDispatch } from '../../app/typedReduxHooks'

type OrganizationCreationForm = {
  hpio: string
  name: string
}

const OrganizationCreationPage = () => {
  const { hpio } = useLocation().state as { hpio: string }
  const navigate = useNavigate()
  const toast = useToast()
  const { selectedRole } = useSelector((state: RootState) => state.onboardingContext)
  const dispatch = useAppDispatch()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      hpio,
      name: '',
    },
  })

  useEffect(() => {
    if (!hpio) {
      navigate(-1)
    }
  }, [hpio, navigate])

  const { trigger: triggerCreateOrganization, loading: createOrgLoading } = useApi(
    createOrganizationByHPIO,
    {
      onSuccess: async (organization) => {
        await dispatch(setOrganizationId(organization.id))
        await dispatch(setClinicId(organization?.clinic?.id))
        if (selectedRole === 'prescriber') {
          navigate(routes.createPrescriberProfile.path)
        } else {
          // Handle admin, PATIENT_ADMIN, and RX_ADMIN roles
          await dispatch(appDidFinishLaunching())
          navigate(routes.settings.path)
        }
      },
      onFailure: (error) => {
        toast({
          title: error.response.data.message ?? 'Failed to create organisation. Please try again.',
          status: 'error',
          duration: 5000,
        })
      },
    }
  )

  const onSubmit = ({ hpio, name }: OrganizationCreationForm) => {
    triggerCreateOrganization({ name, hpio })
  }

  const StyledInputField = styled(InputField)`
    text-align: left;
  `

  return (
    <Page alignItems="center" containerProps={{ height: '100%' }}>
      <VStack height="100%" spacing="12" maxWidth="700px" width="100%" p="24px">
        <Center>
          <Logo maxWidth={150} />
        </Center>
        <Flex
          direction="column"
          gap="6"
          shadow="2xl"
          borderRadius="md"
          p="12"
          mt="12"
          width="80%"
          align="center"
          textAlign="center"
        >
          <Heading as="h2" alignSelf="center" size="lg">
            Create a New Organisation
          </Heading>
          <InputField label="HPI-O Number" field="hpio">
            <Input {...register('hpio')} value={hpio} isDisabled></Input>
          </InputField>
          <StyledInputField label="Organisation Name" field="name" isRequired errors={errors}>
            <Input
              {...register('name', {
                setValueAs: trimValue,
                required: 'Please enter organisation name',
              })}
              placeholder="Enter organisation name"
            />
          </StyledInputField>
          <Button
            width="100%"
            colorScheme="blue"
            onClick={handleSubmit(onSubmit)}
            disabled={Object.keys(errors).length > 0}
            isLoading={createOrgLoading}
          >
            Create and Join
          </Button>
          <Button width="100%" colorScheme="blue" onClick={() => navigate(-1)} variant="outline">
            Back
          </Button>
        </Flex>
      </VStack>
    </Page>
  )
}

export default OrganizationCreationPage
